import { ButtonHTMLAttributes, FC } from 'react'

/** Visual variants for the shared button component. */
type ButtonVariant = 'primary' | 'secondary' | 'ghost'
/** Supported size tokens for the shared button component. */
type ButtonSize = 'sm' | 'md'

/** Props for the shared button component. */
type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: '!text-brand-smoky',
  secondary: '!text-brand-azure',
  ghost: '!text-brand-azure',
}

const variantStyles: Record<ButtonVariant, { ['--btn-bg']: string; ['--btn-hover']: string }> = {
  primary: { '--btn-bg': 'var(--color-gold-primary)', '--btn-hover': 'var(--color-gold-hover)' },
  secondary: { '--btn-bg': 'var(--color-blue-primary)', '--btn-hover': 'var(--color-blue-hover)' },
  ghost: { '--btn-bg': 'var(--color-blue-primary)', '--btn-hover': 'var(--color-blue-hover)' },
}

const textColorStyles: Record<ButtonVariant, string> = {
  primary: 'var(--color-black-primary)',
  secondary: 'var(--color-blue-light)',
  ghost: 'var(--color-blue-light)',
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-sm',
}

/**
 * Reusable button with consistent styling and accessible defaults.
 * Defaults to `type="button"` unless a `type` prop is provided.
 */
export const Button: FC<ButtonProps> = ({
  className,
  variant = 'secondary',
  size = 'sm',
  type,
  style,
  ...props
}) => {
  const classes = [
    'rounded-lg bg-[var(--btn-bg)] font-semibold uppercase tracking-[0.3em] transition-colors duration-150 hover:bg-[var(--btn-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 focus-visible:ring-offset-brand-azure',
    variantClasses[variant],
    sizeClasses[size],
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      className={classes}
      style={{ ...variantStyles[variant], color: textColorStyles[variant], ...style }}
      type={type ?? 'button'}
      {...props}
    />
  )
}
